import * as React from 'react';
import { Location, NavigateFunction, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';

// ponytail: v5-shaped history/withRouter shim over v6 hooks, per the official
// migration guide. Call sites keep v5 idioms; replace with hooks when files are touched.
export type CompatTo = string | { pathname: string; search?: string; state?: any };

export interface History {
  location: Location;
  push(to: CompatTo): void;
  replace(to: CompatTo): void;
  goBack(): void;
}

export interface match<P = {}> {
  params: P;
}

export interface RouteComponentProps<P = {}> {
  history: History;
  location: Location;
  match: match<P>;
}

export type RouteChildrenProps<P = {}> = RouteComponentProps<P>;

const go = (navigate: NavigateFunction, to: CompatTo, replace: boolean) => {
  if (typeof to === 'string') {
    navigate(to, { replace });
  } else {
    navigate({ pathname: to.pathname, search: to.search }, { replace, state: to.state });
  }
};

const makeHistory = (navigate: NavigateFunction, location: Location): History => ({
  location,
  push: (to) => go(navigate, to, false),
  replace: (to) => go(navigate, to, true),
  goBack: () => navigate(-1),
});

export function useHistory(): History {
  const navigate = useNavigate();
  const location = useLocation();
  return React.useMemo(() => makeHistory(navigate, location), [navigate, location]);
}

// Return type is deliberately loose: several call sites declare their connect() ownProps as the
// full prop set, which never type-checked strictly under v5 either.
export function withRouter<P extends RouteComponentProps<any>>(Component: React.ComponentType<P>): React.ComponentType<any> {
  return (props) => {
    const history = useHistory();
    const location = useLocation();
    const params = useParams();
    return <Component {...(props as P)} history={history} location={location} match={{ params: params as any }} />;
  };
}

export function useCompanyAccountParams(): { companyId?: number; accountId?: number } {
  const withAccount = useMatch('/company/:companyId/account/:accountId/*');
  const companyOnly = useMatch('/company/:companyId/*');
  const params = withAccount?.params ?? companyOnly?.params;
  const parse = (value?: string) => {
    const parsed = value ? parseInt(value, 10) : NaN;
    return Number.isNaN(parsed) ? undefined : parsed;
  };
  return { companyId: parse(params?.companyId), accountId: parse(withAccount?.params.accountId) };
}
